import { memo } from 'react'
import {
  DatePicker,
  MultiSelectChip,
  PhoneInput,
  Select,
  Stack,
  TextField,
} from 'ui/baseComponents'
import { FORM_FIELD_WIDTH_UNITS } from 'ui/screens/EditPatient/PatientDetails/consts'
import { getFieldWidth } from './helpers'
import { getNestedObjectValue } from 'utilities/objects'

const FormField = ({
  errors,
  field,
  formDataDemographicData,
  formDataPatientCodeName,
  formDataPatientContactInfo,
  handleChange,
  handleDateChange,
  handleMultiSelectChange,
  isEditing,
  loadingOptions,
  options,
  patient,
}) => {
  const { datePickerProps, isOnlyFieldInRow, key, label, layout, name, type } =
    field

  const STYLES = {
    datePicker: {
      width: '100%',
    },
  }

  /* Iterate through the sources and return the first non-empty value
   * If all sources are empty, return an empty string
   * Accounts for cases where the user has updated the value to null or an empty string
   */
  const getValueFromSources = ({ sources, path }) => {
    for (const source of sources) {
      const value = getNestedObjectValue({ object: source, path })
      // eslint-disable-next-line no-undefined
      if (value !== undefined) {
        return value
      }
    }
    return ''
  }

  const commonProps = {
    label,
    name,
    value: getValueFromSources({
      sources: [
        formDataPatientCodeName,
        formDataDemographicData,
        formDataPatientContactInfo,
        patient,
      ],
      path: name,
    }),
    onChange: (event) => handleChange({ event, key }),
    error: !!errors[name],
    helperText: errors[name],
  }

  const readOnly = !isEditing && !!commonProps.value

  const fieldComponent = (() => {
    switch (type) {
      case 'text':
      case 'email':
        return (
          <TextField
            {...commonProps}
            type={type}
            disabled={!isEditing}
            inputProps={{
              readOnly,
            }}
          />
        )
      case 'select':
        return (
          <Select
            {...commonProps}
            options={options[key] || []}
            disabled={!isEditing || loadingOptions}
            readOnly={readOnly}
          />
        )
      case 'date':
        return (
          <DatePicker
            {...commonProps}
            onChange={handleDateChange}
            disabled={!isEditing}
            readOnly={readOnly}
            slotProps={{
              textField: {
                helperText: errors[name],
                error: !!errors[name],
              },
            }}
            sx={{
              ...STYLES.datePicker,
            }}
            {...datePickerProps}
          />
        )
      case 'multiSelect':
        return (
          <MultiSelectChip
            {...commonProps}
            selectedValues={
              getNestedObjectValue({
                object: formDataDemographicData,
                path: name,
              }) ||
              getNestedObjectValue({ object: patient, path: name }) ||
              []
            }
            options={options[key] || []}
            onChange={handleMultiSelectChange}
            disabled={!isEditing}
            readOnly={readOnly}
          />
        )
      case 'phone':
        return (
          <PhoneInput
            {...commonProps}
            disabled={!isEditing}
            readOnly={readOnly}
          />
        )
      default:
        return null
    }
  })()

  return (
    <Stack
      sx={{
        width: getFieldWidth({
          layout,
          unit: FORM_FIELD_WIDTH_UNITS.percentage,
        }),
      }}
      direction="row"
    >
      {fieldComponent}
      {isOnlyFieldInRow && <div />}
    </Stack>
  )
}

export default memo(FormField)
